import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined';
import { BASE_URL_API } from '../Constants';
import { useAuth } from '../hooks/AuthProvider';
import { LanguageContext } from '../context/LanguageContext';
import orgTranslations from '../languages/organization.json';
import { getErrorMessage } from '../utils/errorUtils';
import Alert from './Alert';
import Spinner from './Spinner';
import { normalizeOrganizationListRow } from '../utils/listRowIds';

export default function TransferOrganizationModal({ state, setState, refresh }) {
  const { permissions } = useAuth();
  const canTransferOrganizations = permissions['transfer_organizations'];
  const { lang } = useContext(LanguageContext);
  const t = orgTranslations?.[lang]?.organizationDetails || {};
  const isRtl = ['ar', 'ur'].includes(lang);

  const [organizations, setOrganizations] = useState([]);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [destinationOrganization, setDestinationOrganization] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    type: 'info',
    position: isRtl ? 'left' : 'right',
  });

  const showAlert = useCallback((type, message) => {
    setAlertState({
      open: true,
      type,
      message,
      position: isRtl ? 'left' : 'right',
    });
  }, [isRtl]);

  const closeModal = useCallback(() => {
    setOrganizations([]);
    setCurrentOrganization(null);
    setDestinationOrganization(null);
    setLoading(false);
    setState({ open: false, id: 0 });
  }, [setState]);

  useEffect(() => {
    if (!state?.open || !state?.id || !canTransferOrganizations) return;

    let mounted = true;
    setLoading(true);

    Promise.all([
      axios.get(`${BASE_URL_API}/organization/get/${state.id}`),
      axios.get(`${BASE_URL_API}/organization/list`),
    ])
      .then(([currentResponse, listResponse]) => {
        if (!mounted) return;

        const currentData =
          currentResponse?.data?.status === 'OK' ? currentResponse.data.data : null;
        const listData =
          listResponse?.data?.status === 'OK' && Array.isArray(listResponse.data.data)
            ? listResponse.data.data.map(normalizeOrganizationListRow)
            : [];

        setCurrentOrganization(currentData);
        setOrganizations(listData);
      })
      .catch((error) => {
        if (!mounted) return;
        setCurrentOrganization(null);
        setOrganizations([]);
        showAlert('error', getErrorMessage(error));
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [canTransferOrganizations, showAlert, state?.id, state?.open]);

  const options = useMemo(() => {
    const currentId = Number(currentOrganization?.id);
    const currentParentId = Number(currentOrganization?.parent);

    const subtreeIds = new Set(
      (organizations || [])
        .filter((org) =>
          Array.isArray(org?.hierarchy) &&
          org.hierarchy.some((entry) => Number(entry?.id) === currentId)
        )
        .map((org) => Number(org.id))
        .filter((id) => Number.isFinite(id))
    );

    return (organizations || [])
      .filter((org) => {
        const orgId = Number(org?.id);
        if (!Number.isFinite(orgId)) return false;
        if (orgId === currentId) return false;
        if (orgId === currentParentId) return false;
        if (subtreeIds.has(orgId)) return false;
        return true;
      })
      .map((org) => ({
        value: org.id,
        label: org.name,
        ...org,
      }));
  }, [currentOrganization, organizations]);

  const selectedDestinationOption = useMemo(
    () => options.find((option) => Number(option.value) === Number(destinationOrganization?.id)) || null,
    [destinationOrganization?.id, options]
  );

  const transferOrganization = async () => {
    if (!currentOrganization?.parent) {
      showAlert('error', t.onlySubOrganizations || 'Only sub-organizations can be transferred.');
      return;
    }

    if (!destinationOrganization?.id) {
      showAlert('error', t.transferOrgError || 'Please select a destination organization.');
      return;
    }

    try {
      const res = await axios.post(
        `${BASE_URL_API}/organization/transfer/${state.id}`,
        { organization_id: destinationOrganization.id },
        { headers: { 'Content-Type': 'application/json' } }
      );

      showAlert('success', res.data?.message || t.transferSuccess || 'Organization transferred successfully.');
      setTimeout(() => {
        closeModal();
        refresh?.(res.data?.data);
      }, 1200);
    } catch (error) {
      showAlert('error', getErrorMessage(error));
    }
  };

  if (!state?.open) return null;

  if (!canTransferOrganizations) {
    return (
      <div id="modal" dir={isRtl ? 'rtl' : 'ltr'}>
        <div id="popup" className="dashboard-modal">
          <div className="dashboard-header">
            <div className="dashboard-header-icon">
              <SyncAltOutlinedIcon />
            </div>
            <div className="dashboard-header-text">
              <h2>{t.permissionDeniedTitle || 'Permission Denied'}</h2>
              <p>{t.permissionDeniedMessage || 'You do not have permission to transfer organizations.'}</p>
            </div>
          </div>
          <div className="modal-divider" />
          <div className="modal-content dashboard-content" />
          <div className="modal-footer dashboard-footer">
            <button className="btn btn-cancel" onClick={closeModal}>
              {t.buttonClose || 'Close'}
            </button>
            <div />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Alert
        open={alertState.open}
        close={() => setAlertState((prev) => ({ ...prev, open: false }))}
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />

      <div id="modal" dir={isRtl ? 'rtl' : 'ltr'}>
        <div id="popup" className="dashboard-modal">
          <div className="dashboard-header">
            <div className="dashboard-header-icon">
              <SyncAltOutlinedIcon />
            </div>
            <div className="dashboard-header-text">
              <h2>{t.transferTitle || 'Transfer Organization'}</h2>
              <p>{t.transferDesc || 'Transfer this sub-organization under another organization.'}</p>
            </div>
          </div>

          <div className="modal-divider" />

          <div className="modal-content dashboard-content">
            {loading ? (
              <Spinner />
            ) : (
              <div className="form-section">
                <div className="form-item">
                  <label className="form-lbl">{t.currentOrganizationLabel || 'Organization'}</label>
                  <input
                    type="text"
                    className="add-column"
                    value={currentOrganization?.name || ''}
                    disabled
                  />
                </div>

                <div className="form-item">
                  <label className="form-lbl">{t.selectOrgLabel || 'Destination organization'}</label>
                  <Select
                    inputId="transfer-organization-destination"
                    options={options}
                    value={selectedDestinationOption}
                    onChange={(selectedOption) => setDestinationOrganization(selectedOption || null)}
                    placeholder={t.selectOrgPlaceholder || 'Select destination organization'}
                    classNamePrefix="react-select"
                    isClearable
                    isRtl={isRtl}
                    isDisabled={!currentOrganization?.parent || options.length === 0}
                  />
                </div>

                {!currentOrganization?.parent ? (
                  <p className="form-name-helper">
                    {t.onlySubOrganizations || 'Only sub-organizations can be transferred.'}
                  </p>
                ) : options.length === 0 ? (
                  <p className="form-name-helper">
                    {t.noTransferTargets || 'No valid destination organizations are available.'}
                  </p>
                ) : null}
              </div>
            )}
          </div>

          <div className="modal-footer dashboard-footer">
            <button className="btn btn-cancel" onClick={closeModal}>
              {t.buttonCancel || 'Cancel'}
            </button>
            <button
              className="btn btn-apply dashboard-primary-btn"
              onClick={transferOrganization}
              disabled={loading || !currentOrganization?.parent || options.length === 0}
            >
              {t.buttonTransfer || 'Transfer'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
